import React, { useState, useEffect } from "react";
import { Clock, ChevronDown, ChevronRight, CheckCircle, XCircle, Camera, MessageSquare, AlertTriangle, Calendar } from "lucide-react";
import { useDispatch, useSelector } from 'react-redux';
import { HitApi } from '../../Api/ApiHit';
import { updateTaskEvidence } from '../../constant/Constant';
import LivePhotoUpload from '../SingleFileUpload/LivePhotoUpload';
import AppButton from '../AppButton/AppButton';
import { getElevateUser } from "../../storage/Storage";
import { setApiJson } from "../../redux/Actions/ApiAction";

const TaskCardIntervals = ({ task, onTaskUpdate, getTodaysTask }) => {

  console.log({ task, onTaskUpdate, getTodaysTask });
  const [showIntervals, setShowIntervals] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [remarks, setRemarks] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const dispatch = useDispatch();

  // Extract task properties - handles both flat and nested structures
  const taskName = task.taskName || task.taskId?.taskName || '';
  const description = task.description || task.taskId?.description || '';
  const priority = task.priority || task.taskId?.priority || '';
  const taskFrequency = task.taskFrequency || task.taskId?.taskFrequency || '';
  const taskIntervals = task.taskIntervals || task.taskId?.taskIntervals || [];

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getIntervalStatus = (interval) => {
    const now = currentTime;

    if (interval.status === 'completed') {
      return 'completed';
    }

    if (now < interval.start) {
      return 'pending';
    } else if (now >= interval.start && now <= interval.end) {
      return 'active';
    } else {
      return 'missed';
    }
  };

  const getRemainingTime = (endTimestamp) => {
    const remaining = endTimestamp - currentTime;
    if (remaining <= 0) return "Time's up!";

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} remaining`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')} remaining`;
  };

  const getTimeUntilStart = (startTimestamp) => {
    const timeUntilStart = startTimestamp - currentTime;
    if (timeUntilStart <= 0) return "Starting now!";

    const hours = Math.floor(timeUntilStart / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilStart % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `Starts in ${hours}h ${minutes}m`;
    }
    return `Starts in ${minutes}m`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <div className="relative"><Camera className="w-4 h-4 text-blue-500" /><div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div></div>;
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'missed':
        return <XCircle className="w-4 h-4 text-gray-400" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "pending":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "active":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "missed":
        return "bg-gray-50 text-gray-600 border-gray-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertTriangle className="w-3 h-3" />
            {isMobile ? 'High' : 'High Priority'}
          </div>
        );
      case 'medium':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            {isMobile ? 'Med' : 'Medium'}
          </div>
        );
      case 'low':
        return (
          <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Low
          </div>
        );
      default:
        return null;
    }
  };

  const handleRemarksChange = (intervalIndex, value) => {
    setRemarks(prev => ({
      ...prev,
      [intervalIndex]: value
    }));
  };

  const handleSubmitEvidence = async (intervalIndex) => {
    try {
      const evidenceUrl = ApiReducer?.apiJson?.evidenceUrl;
      if (!evidenceUrl) {
        alert("Please upload a photo first");
        return;
      }

      const intervalRemarks = remarks[intervalIndex] || '';
      const updatedTaskIntervals = [...taskIntervals];
      updatedTaskIntervals[intervalIndex] = {
        ...updatedTaskIntervals[intervalIndex],
        taskEvidenceUrl: evidenceUrl,
        status: "completed",
        remarks: intervalRemarks,
        submittedBy: getElevateUser()?._id
      };

      const updatePayload = {
        _id: task._id,
        taskIntervals: updatedTaskIntervals
      };

      const response = await HitApi(updatePayload, updateTaskEvidence);

      if (response.success || response.data) {
        dispatch(setApiJson({}));
        getTodaysTask();
        setRemarks(prev => {
          const updated = { ...prev };
          delete updated[intervalIndex];
          return updated;
        });

        if (onTaskUpdate) {
          onTaskUpdate(task._id, updatedTaskIntervals);
        }

        alert("Task evidence uploaded successfully!");
      }

    } catch (error) {
      console.error("Error submitting evidence:", error);
      alert("Error submitting evidence. Please try again.");
    }
  };

  const hasActiveInterval = taskIntervals.some(interval =>
    getIntervalStatus(interval) === 'active'
  );

  const completedCount = taskIntervals.filter(interval =>
    getIntervalStatus(interval) === 'completed'
  ).length || 0;

  const totalIntervals = taskIntervals.length || 0;

  return (
    <div className={`group relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 hover:shadow-xl ${hasActiveInterval
        ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-blue-100'
        : 'bg-white border border-gray-100'
      } ${isMobile ? 'p-3 mb-3' : 'p-6 mb-4'}`}>

      {/* Active indicator */}
      {hasActiveInterval && (
        <div className="absolute top-0 right-0 w-0 h-0 border-l-[40px] border-l-transparent border-t-[40px] border-t-blue-500">
          <div className="absolute -top-8 -right-2 text-white text-xs font-bold rotate-45">
            LIVE
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-1">
              <h3 className={`font-bold text-gray-900 mb-2 ${isMobile ? 'text-lg leading-tight' : 'text-xl'}`}>
                {taskName}
              </h3>
              <p className={`text-gray-600 mb-3 ${isMobile ? 'text-sm' : 'text-base'}`}>
                {isMobile && description?.length > 80
                  ? `${description.substring(0, 80)}...`
                  : description
                }
              </p>
            </div>
          </div>

          {/* Meta Information */}
          <div className={`flex flex-wrap items-center gap-2 ${isMobile ? 'mb-2' : 'mb-4'}`}>
            {getPriorityBadge(priority)}

            <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
              <Calendar className="w-3 h-3" />
              {taskFrequency}
            </div>

            {totalIntervals > 0 && (
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${completedCount === totalIntervals
                  ? 'bg-emerald-100 text-emerald-700'
                  : hasActiveInterval
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                <CheckCircle className="w-3 h-3" />
                {completedCount}/{totalIntervals} Done
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {totalIntervals > 0 && (
        <div className="mb-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-500 ${completedCount === totalIntervals
                  ? 'bg-emerald-500'
                  : hasActiveInterval
                    ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                    : 'bg-gray-400'
                }`}
              style={{ width: `${(completedCount / totalIntervals) * 100}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Progress: {Math.round((completedCount / totalIntervals) * 100)}% completed
          </p>
        </div>
      )}

      {/* Accordion Section */}
      {taskIntervals && taskIntervals.length > 0 && (
        <div>
          <button
            onClick={() => setShowIntervals(!showIntervals)}
            className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 ${hasActiveInterval
                ? 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 hover:from-blue-200 hover:to-indigo-200'
                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              } ${isMobile ? 'text-sm' : 'text-base'}`}
          >
            <div className="flex items-center gap-3">
              <Clock className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
              <span className="font-semibold">
                Time Intervals ({taskIntervals.length})
              </span>
              {hasActiveInterval && (
                <div className="flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded-full text-xs font-bold animate-pulse">
                  <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                  ACTIVE
                </div>
              )}
            </div>
            {showIntervals ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>

          {/* Intervals List */}
          {showIntervals && (
            <div className={`space-y-3 ${isMobile ? 'mt-3' : 'mt-4'}`}>
              {taskIntervals.map((interval, index) => {
                const intervalStatus = getIntervalStatus(interval);
                const isActive = intervalStatus === 'active';

                return (
                  <div
                    key={index}
                    className={`relative overflow-hidden rounded-xl transition-all duration-300 ${intervalStatus === 'completed'
                        ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200'
                        : isActive
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 shadow-lg'
                          : intervalStatus === 'pending'
                            ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200'
                            : 'bg-gray-50 border-2 border-gray-200'
                      } ${isMobile ? 'p-3' : 'p-4'}`}
                  >
                    {/* Status indicator stripe */}
                    <div className={`absolute top-0 left-0 w-full h-1 ${intervalStatus === 'completed' ? 'bg-emerald-500'
                        : isActive ? 'bg-blue-500 animate-pulse'
                          : intervalStatus === 'pending' ? 'bg-amber-500'
                            : 'bg-gray-400'
                      }`}></div>

                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusIcon(intervalStatus)}
                          <span className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
                            {interval.interval}
                          </span>
                        </div>

                        <div className={`text-gray-600 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3" />
                            {formatTime(interval.start)} - {formatTime(interval.end)}
                          </div>
                        </div>

                        <p className={`text-gray-500 mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {new Date(interval.start).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>

                        {isActive && (
                          <div className="flex items-center gap-2 text-blue-600 font-semibold animate-pulse">
                            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
                            <span className={isMobile ? 'text-xs' : 'text-sm'}>
                              {getRemainingTime(interval.end)}
                            </span>
                          </div>
                        )}

                        {intervalStatus === 'pending' && (
                          <p className={`text-amber-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            ⏳ {getTimeUntilStart(interval.start)}
                          </p>
                        )}

                        {intervalStatus === 'completed' && (
                          <p className={`text-emerald-600 font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            Task Completed Successfully
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col items-end">
                        {intervalStatus === 'completed' && (
                          <div className={`bg-emerald-500 text-white px-3 py-1 rounded-full font-bold flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            <CheckCircle className="w-3 h-3" />
                            DONE
                          </div>
                        )}
                        {isActive && (
                          <div className={`bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-3 py-1 rounded-full font-bold animate-pulse flex items-center gap-1 shadow-lg ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            <div className="w-2 h-2 bg-white rounded-full animate-ping"></div>
                            LIVE
                          </div>
                        )}
                        {intervalStatus === 'pending' && (
                          <div className={`bg-amber-500 text-white px-3 py-1 rounded-full font-medium flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            <Clock className="w-3 h-3" />
                            Pending
                          </div>
                        )}
                        {intervalStatus === 'missed' && (
                          <div className={`bg-gray-500 text-white px-3 py-1 rounded-full font-medium flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                            <XCircle className="w-3 h-3" />
                            Missed
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Evidence Upload for Active Intervals */}
                    {isActive && (
                      <div className={`border-t-2 border-blue-200 ${isMobile ? 'mt-3 pt-3' : 'mt-4 pt-4'}`}>
                        <div className="bg-white rounded-xl p-4 border-2 border-dashed border-blue-300 shadow-inner">
                          <div className="text-center mb-4">
                            <div className="relative inline-block">
                              <Camera className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                            </div>
                            <h5 className={`font-bold text-blue-700 mb-1 ${isMobile ? 'text-sm' : 'text-base'}`}>
                              Upload Evidence Photo
                            </h5>
                          </div>
                          <LivePhotoUpload
                            name="evidenceUrl"
                            className="w-full mb-4"
                          />

                          <div className="mb-4 mt-2">
                            <label className={`block font-semibold text-blue-700 mb-2 ${isMobile ? 'text-sm' : 'text-base'}`}>
                              <MessageSquare className="w-4 h-4 inline mr-2" />
                              Add Remarks (Optional)
                            </label>
                            <textarea
                              value={remarks[index] || ''}
                              onChange={(e) => handleRemarksChange(index, e.target.value)}
                              placeholder="Enter any additional notes or observations..."
                              className={`w-full p-3 border-2 border-blue-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all ${isMobile ? 'text-sm' : 'text-base'}`}
                              rows={isMobile ? "2" : "3"}
                            />
                          </div>

                          <AppButton
                            buttontext="Submit Evidence"
                            onClick={() => handleSubmitEvidence(index)}
                            className={`w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl ${isMobile ? 'text-sm' : 'text-base'}`}
                          />
                        </div>
                      </div>
                    )}

                    {/* Completed Evidence Display */}
                    {intervalStatus === 'completed' && (
                      <div className={`border-t-2 border-emerald-200 ${isMobile ? 'mt-3 pt-3' : 'mt-4 pt-4'}`}>
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                            <span className={`font-bold text-emerald-700 ${isMobile ? 'text-sm' : 'text-base'}`}>
                              Evidence Submitted Successfully
                            </span>
                          </div>

                          {interval.remarks && (
                            <div className="mb-4">
                              <p className={`text-emerald-600 font-semibold mb-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                <MessageSquare className="w-4 h-4 inline mr-1" />
                                Remarks:
                              </p>
                              <div className="bg-white p-3 rounded-lg border border-emerald-200 shadow-sm">
                                <p className={`text-emerald-800 ${isMobile ? 'text-sm' : 'text-base'}`}>
                                  {interval.remarks}
                                </p>
                              </div>
                            </div>
                          )}

                          {interval.taskEvidenceUrl &&
                            interval.taskEvidenceUrl !== 'waiting' &&
                            interval.taskEvidenceUrl.includes('http') && (
                              <div className="text-center">
                                <p className={`text-emerald-600 mb-3 font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                  Evidence Photo:
                                </p>
                                <div className="relative inline-block">
                                  <img
                                    src={interval.taskEvidenceUrl}
                                    alt="Task Evidence"
                                    className={`object-cover rounded-xl border-4 border-emerald-300 shadow-lg ${isMobile ? 'max-w-full h-32' : 'max-w-full h-40'}`}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <div className="absolute top-2 right-2 bg-emerald-500 text-white p-1 rounded-full">
                                    <CheckCircle className="w-4 h-4" />
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskCardIntervals;